/**
 * useMailboxHistory — a small undo/redo command stack for mailbox actions.
 *
 * Every reversible action (mark read/unread, star/unstar, move, archive,
 * delete=move-to-trash — single OR bulk) is recorded as a command carrying an
 * `undo` and a `redo` thunk. The thunks call the SAME optimistic mutation hooks
 * the forward action used, with data captured at record time (previous flag
 * values, source folders), so undo/redo just re-issue real mutations — the
 * existing optimistic-patch + invalidate-on-settle machinery reconciles the
 * cache. The local Vault row is updated synchronously by each mutation, so an
 * undo's refetch always reflects the corrected state even though the IMAP
 * write-back is async.
 *
 * Permanent actions (Empty Trash) are intentionally NOT recorded — they're
 * irreversible by design.
 *
 * Stacks live in refs (not state) so the thunks never see a stale closure and
 * we don't nest setState updaters; a version counter drives re-render for the
 * `canUndo` / `canRedo` / label getters the toolbar reads.
 */

import { useCallback, useReducer, useRef } from 'react';

export interface MailboxHistoryEntry {
  /** Human label for the toolbar tooltip / toast (e.g. "Deleted 3 messages"). */
  label: string;
  /** Reverse the action. */
  undo: () => void;
  /** Re-apply the action. */
  redo: () => void;
}

export interface MailboxHistory {
  record: (entry: MailboxHistoryEntry) => void;
  undo: () => void;
  redo: () => void;
  clear: () => void;
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
}

const DEFAULT_LIMIT = 50;

export function useMailboxHistory(limit = DEFAULT_LIMIT): MailboxHistory {
  const undoRef = useRef<MailboxHistoryEntry[]>([]);
  const redoRef = useRef<MailboxHistoryEntry[]>([]);
  const [, bump] = useReducer((x: number) => x + 1, 0);

  const record = useCallback(
    (entry: MailboxHistoryEntry) => {
      undoRef.current = [...undoRef.current, entry].slice(-limit);
      // A fresh action invalidates the redo branch.
      redoRef.current = [];
      bump();
    },
    [limit],
  );

  const undo = useCallback(() => {
    const entry = undoRef.current[undoRef.current.length - 1];
    if (!entry) return;
    undoRef.current = undoRef.current.slice(0, -1);
    redoRef.current = [...redoRef.current, entry].slice(-limit);
    entry.undo();
    bump();
  }, [limit]);

  const redo = useCallback(() => {
    const entry = redoRef.current[redoRef.current.length - 1];
    if (!entry) return;
    redoRef.current = redoRef.current.slice(0, -1);
    undoRef.current = [...undoRef.current, entry].slice(-limit);
    entry.redo();
    bump();
  }, [limit]);

  const clear = useCallback(() => {
    undoRef.current = [];
    redoRef.current = [];
    bump();
  }, []);

  return {
    record,
    undo,
    redo,
    clear,
    canUndo: undoRef.current.length > 0,
    canRedo: redoRef.current.length > 0,
    undoLabel: undoRef.current[undoRef.current.length - 1]?.label ?? null,
    redoLabel: redoRef.current[redoRef.current.length - 1]?.label ?? null,
  };
}
